<template>
  <div>
    <div class="grid">
      <div v-for="key in orderedKeys"
           :key="key"
      >
        <label :for="`${formId}-${key}`">
          {{ labels[key].label }}
          <span
            v-if="labels[key].options.indexOf('required') >= 0"
            v-help.mouse="'Tato vlastnost musí být vyplněná.'"
            :class="[`label-option`, `label-option-required`]"
          >
            *
          </span>
          <span
            v-if="labels[key].options.indexOf('autofilled') >= 0"
            v-help.mouse="'Doplněno automaticky na základě nastavení této vlastnosti.'"
            :class="[`label-option`, `label-option-autofilled`]"
          >
            (automaticky)
          </span>
          <span
            v-if="labels[key].options.indexOf('server-calculated') >= 0"
            v-help.mouse="'Bude automaticky dopočítáno (doplněno) po uložení. Toto doplnění může chvíli trvat a může vyžadovat refresh stránky'"
            :class="[`label-option`, `label-option-server-calculated`]"
          >
            (automaticky po uložení)
          </span>
          <span v-if="isHidden[key]">
            <i v-help.mouse="`Hodnota této vlastnosti je pro vás skrytá.`"
               class="fas fa-eye-slash hidden-value"
            ></i>
          </span>
        </label>
        <InputProxy
          :id="key"
          :htmlId="`${formId}-${key}`"
          :value="value[key]"
          :meta="meta"
          :lock="locks[key]"
          :editType="editType"
          @input="input(key, $event)"
          @labelOptions="labelOptions(key, $event)"
          @isHidden="handleIsHidden(key, $event)"
          @delete="onDelete"
        ></InputProxy>
        <span
          v-if="lockLabels[key]"
          style="color: red; font-weight: bold; font-size: 0.8rem"
        >
          <i class="fas fa-lock"></i>
          {{ lockLabels[key] }}
        </span>
      </div>
    </div>

    <div v-if="customProperties"
         class="control-grid"
    >
      <select class="custom-select"
              :value="'#'"
              @input="addCustomProperty"
      >
        <option value="#"
                disabled="disabled"
        >
          <i class="fas fa-plus-circle"></i>
          Přidat vlastnost
        </option>
        <option v-for="k in missingKeys"
                :key="k.key"
                :value="k.key"
        >
          {{ k.name }}
        </option>
      </select>
    </div>

    <div class="control-grid">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Vue from 'vue'
import InputProxy from 'CORE/components/form/input-proxy'
import { uuid } from 'vue-uuid'
import moment from 'moment'

export default {
    name: 'PropertyForm',
    components: {
        InputProxy,
    },
    props: {
        value: {
            required: false,
            type: Object,
            default: _ => {return {}},
        },
        propertyKeys: {
            required: false,
            type: Array,
            default: _ => [],
        },
        customProperties: {
            required: false,
            type: Boolean,
            default: _ => false,
        },
        meta: {
            required: false,
            type: Object,
            default: _ => {return {}},
        },
        editType: {
            required: false,
            type: String,
            default: _ => 'edit',
            validator: value => {
                if (_.indexOf(['edit', 'new'], value) === -1) {
                    console.warn('editType MUST be one of [new, edit]')
                    return false
                }
                return true
            },
        },
    },
    data() {
        return {
            customPropertyKeys: [],
            labelsOptions: {},
            now: 0,
            isHidden: {},
        }
    },
    computed: {
        keys() {
            return _.union(_.union(_.keys(this.value), this.propertyKeys), this.customPropertyKeys)
        },
        orderedKeys() {
            return _.sortBy(this.keys, [
                k => _.toNumber(_.get(this.$store.getters.propertyTypeByKey(k), 'settings.order', '0')),
                k => k,
            ])
        },
        labels() {
            return _.chain(this.keys)
                .mapKeys(k => k)
                .mapValues(k => {
                    return {
                        label: _.get(this.$store.getters.propertyTypeByKey(k), 'name'),
                        options: _.get(this.labelsOptions, k, []),
                    }
                })
                .value()
        },
        formId() {
            return uuid.v4()
        },
        missingKeys() {
            return _.chain(this.$store.getters.propertyTypes)
                .map(p => p.key)
                .difference(this.keys)
                .map(k => this.$store.getters.propertyTypeByKey(k))
                .value()
        },
        locks() {
            return _.pickBy(_.get(this.$store.getters.locks, this.entity, {}), lock => {
                const user = _.get(lock, 'user')
                const time = _.get(lock, 'time')
                if (_.isString(user) && _.isNumber(time)) {
                    const since = this.now - time
                    return since < 60
                }
                return false
            })
        },
        lockLabels() {
            return _.chain(this.keys)
                .mapKeys(k => k)
                .mapValues(k => {
                    if (_.get(this.locks, k, false)) {
                        const lock = _.get(this.locks, k, {})
                        const time = moment(_.get(lock, 'time'), 'X')
                        const user = this.$store.getters.userById(lock.user)
                        return `${user.username} [${time.fromNow()}]`
                    }
                    return false
                })
                .value()
        },
        entity() {
            return this.$route.params.id
        },
    },
    mounted() {
        this.now = Math.floor(Date.now() / 1000)
        setInterval(_ => {
            this.now = Math.floor(Date.now() / 1000)
        }, 1000)
    },
    methods: {
        input(key, e) {
            Vue.set(this.value, key, e) // TODO zkontrolovat reaktivitu protoze tohle naprimo mutuje value objekt
            this.$emit('input', this.value) // TODO tohle by melo fungovat ale nefunguje
        },
        labelOptions(key, e) {
            Vue.set(this.labelsOptions, key, e)
        },
        handleIsHidden(key, e) {
            Vue.set(this.isHidden, key, e)
        },
        addCustomProperty(e) {
            this.customPropertyKeys.push(e.target.value)
        },
        onDelete(e) {
            this.$emit('input', _.pickBy(this.value, (v, k) => k !== e))
        },
    },
}
</script>

<style scoped lang="scss">
@media (min-width: 0px) and (max-width: 767px) {
  .grid {
    display: grid;
    grid-auto-rows: minmax(60px, auto);
    grid-template-columns: 1fr;
    grid-gap: 5px 5px;
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .grid {
    display: grid;
    grid-auto-rows: minmax(60px, auto);
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 0px 10px;
  }
}

@media (min-width: 1200px) {
  .grid {
    display: grid;
    grid-auto-rows: minmax(60px, auto);
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 5px 20px;
  }
}

label {
    .label-option:not(.label-option-required) {
        color: var(--color-primary);
        font-size: 0.7rem;
        font-style: italic;
        line-height: 1rem;
        vertical-align: super;
        vertical-align: text-top;
        cursor: help;
    }
}

.locked {
    background: repeating-linear-gradient(135deg, rgba(241,247,52,0.2) 0, rgba(241,247,52,0.2) 20px, rgba(0,0,0,0.2) 21px, rgba(0,0,0,0.2) 40px);
    border-radius: 10px;
    padding: 5px;
}

.hidden-value {
    color: orangered;
}
</style>
